import React, { useState, useRef } from 'react';
import { Upload, AlertTriangle, CheckCircle, Loader2 } from 'lucide-react';
import { SessionService } from '../services/sessionService';
import { useAuth } from '../services/AuthContext';

const isDevelopment = process.env.NODE_ENV === 'development';
const API_BASE_URL = isDevelopment ? 'http://localhost:7777' : '';

interface OrphanSessionRecoveryProps {
  sessionId: string;
  filename: string;
  transcriptionChecksum: string;
  onRecoverySuccess: () => void;
  className?: string;
}

const OrphanSessionRecovery: React.FC<OrphanSessionRecoveryProps> = ({
  sessionId,
  filename,
  transcriptionChecksum,
  onRecoverySuccess,
  className = ''
}) => {
  const { token } = useAuth();
  const [isUploading, setIsUploading] = useState(false);
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['audio/mpeg', 'audio/mp3', 'audio/wav', 'audio/mp4', 'audio/m4a'];
    if (!validTypes.includes(file.type) && !file.name.match(/\.(mp3|wav|m4a|mp4)$/i)) {
      setError('Please select a valid audio file (MP3, WAV, M4A, MP4)');
      return;
    }

    setIsUploading(true);
    setError(null);

    try {
      console.log('[ORPHAN-RECOVERY] Starting upload process for file:', file.name, 'Size:', file.size);
      
      const formData = new FormData();
      formData.append('audio', file);

      console.log('[ORPHAN-RECOVERY] Sending request to:', `/api/recover-session-audio/${sessionId}`);
      
      const response = await fetch(`${API_BASE_URL}/api/recover-session-audio/${sessionId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      console.log('[ORPHAN-RECOVERY] Response status:', response.status, response.statusText);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
        console.error('[ORPHAN-RECOVERY] Server error response:', errorData);
        throw new Error(errorData.error || `Server error: ${response.status} ${response.statusText}`);
      }

      const result = await response.json();
      console.log('[ORPHAN-RECOVERY] Audio recovery successful:', result);
      console.log('[ORPHAN-RECOVERY] S3 key:', result.s3Key);
      console.log('[ORPHAN-RECOVERY] File size uploaded:', result.fileSize);

      setUploadSuccess(true);
      setTimeout(() => {
        onRecoverySuccess();
      }, 1500);

    } catch (error) {
      console.error('[ORPHAN-RECOVERY] Upload failed:', error);
      setError(error instanceof Error ? error.message : 'Upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  if (uploadSuccess) {
    return (
      <div className={`bg-green-50 border border-green-200 rounded-lg p-6 ${className}`}>
        <div className="flex items-center space-x-3">
          <CheckCircle className="h-8 w-8 text-green-600" />
          <div>
            <h3 className="text-lg font-semibold text-green-800">Audio Recovered Successfully!</h3>
            <p className="text-green-700">The audio file has been uploaded and linked to this session.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`bg-amber-50 border border-amber-200 rounded-lg p-6 ${className}`}>
      <div className="flex items-start space-x-4">
        <AlertTriangle className="h-8 w-8 text-amber-600 flex-shrink-0 mt-1" />
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-amber-800 mb-2">Missing Audio File</h3>
          <p className="text-amber-700 mb-4">
            This session appears to be missing its original audio file. This can happen for sessions created 
            before our S3 storage migration. To restore full functionality including audio playback and speaker 
            samples, please upload the original audio file.
          </p>
          
          <div className="space-y-3">
            <div className="text-sm text-amber-600">
              <p><strong>Session:</strong> {filename}</p>
              <p><strong>Expected file:</strong> Audio file that matches this transcript</p>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            <button
              onClick={triggerFileSelect}
              disabled={isUploading}
              className="inline-flex items-center px-4 py-2 bg-amber-600 text-white rounded-md hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isUploading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Audio File
                </>
              )}
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*,.mp3,.wav,.m4a,.mp4"
              onChange={handleFileSelect}
              className="hidden"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrphanSessionRecovery;